import { SEO } from "@/components/SEO";
import { Shield } from "lucide-react";

const sections = [
  {
    title: "1. User Consent",
    items: [
      "1.1. By using our website, you consent to the collection, use, and sharing of your personal information as described in this Privacy Policy.",
    ],
  },
  {
    title: "2. Information We Collect",
    items: [
      "2.1. Personal Information: When you place an order, register an account, or contact us, we collect personal details such as your name, email address, phone number, and shipping address.",
      "2.2. Payment Information: Payment processing is handled securely by third-party providers. We do not store your payment details.",
      "2.3. Browsing Data: We may collect cookies, IP addresses, and other browsing information to enhance user experience and analyse website performance.",
    ],
  },
  {
    title: "3. How We Use Your Information",
    items: [
      "3.1. To process and fulfill your orders.",
      "3.2. To communicate with you about your orders, promotions, or updates.",
      "3.3. To improve our website, customer service, and product offerings.",
      "3.4. To prevent fraudulent activities and enhance website security.",
      "3.5. To comply with legal requirements and business operations.",
    ],
  },
  {
    title: "4. Data Protection and Security",
    items: [
      "4.1. We implement strict security measures to protect your personal information from unauthorized access, loss, or misuse.",
      "4.2. We do not sell, trade, or rent your personal information to third parties.",
      "4.3. Your information is shared only with trusted service providers, such as payment processors and shipping partners, to complete transactions.",
    ],
  },
  {
    title: "5. Data Breach Notification",
    items: [
      "5.1. In the event of a data breach, we will notify affected users promptly via email and post a notice on our website, in accordance with applicable legal requirements.",
    ],
  },
  {
    title: "6. Third-Party Sharing",
    items: [
      "6.1. We share your information with trusted service providers, including payment gateways, logistics partners, and marketing platforms, solely for order fulfillment and customer communication purposes.",
    ],
  },
  {
    title: "7. Data Retention Policy",
    items: [
      "7.1. We retain personal information only as long as necessary to fulfill the purposes outlined in this policy or as required by applicable laws. Customer data related to transactions is retained for accounting and legal compliance purposes for up to 10 years.",
    ],
  },
  {
    title: "8. Cookies and Tracking Technologies",
    items: [
      "8.1. We use cookies for website functionality, analytics, and marketing.",
      "8.2. You can manage cookie preferences through your browser settings.",
    ],
  },
  {
    title: "9. Third-Party Links",
    items: [
      "9.1. Our website may contain links to third-party websites. We are not responsible for their privacy policies or practices.",
    ],
  },
  {
    title: "10. Children's Privacy",
    items: [
      "10.1. Our website is not intended for children under 18. We do not knowingly collect personal information from children. If we become aware that a child has provided us with personal information, we will take steps to delete it.",
    ],
  },
  {
    title: "11. Legal Jurisdiction and Compliance",
    items: [
      "11.1. This Privacy Policy is governed by the laws of Telangana, India. We comply with applicable data protection regulations, including India's Digital Personal Data Protection Act (DPDP Act).",
    ],
  },
  {
    title: "12. Your Rights",
    items: [
      "12.1. You have the right to access, update, or request deletion of your personal information.",
      "12.2. You can opt-out of marketing emails by clicking the unsubscribe link.",
      "12.3. To exercise these rights, contact us at info@prarohagreens.com.",
    ],
  },
  {
    title: "13. Changes to This Policy",
    items: [
      "13.1. We reserve the right to update this Privacy Policy at any time.",
      "13.2. Any changes will be posted on this page, and continued use of our website constitutes acceptance of the revised policy.",
    ],
  },
];

export default function PrivacyPolicyPage() {
  return (
    <div className="pt-16 md:pt-20 lg:pt-24" data-testid="privacy-policy-page">
      <SEO title="Privacy Policy" description="Praroha Greens privacy policy — how we collect, use, and protect your personal information." path="/privacy" />

      {/* Hero */}
      <section className="bg-praroha-forest py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-3">Privacy Policy</h1>
          <p className="text-white/70 text-sm md:text-base max-w-2xl mx-auto">
            Welcome to Praroha Greens! Your privacy is important to us. This Privacy Policy explains how we collect, use, and protect your personal information when you visit our website (www.prarohagreens.com) and purchase our microgreens products.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          {sections.map((section) => (
            <div key={section.title}>
              <h2 className="font-heading text-lg md:text-xl font-semibold text-praroha-forest mb-4">{section.title}</h2>
              <ul className="space-y-3">
                {section.items.map((item, i) => (
                  <li key={i} className="text-sm md:text-base text-muted-foreground leading-relaxed">{item}</li>
                ))}
              </ul>
            </div>
          ))}

          <div className="pt-6 border-t border-border">
            <p className="text-sm text-muted-foreground">
              For any privacy-related inquiries, please contact us at{" "}
              <a href="mailto:info@prarohagreens.com" className="text-praroha-forest font-medium hover:underline">info@prarohagreens.com</a>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
